import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import messageService from '../../services/messageService';
import Waveform from './Waveform';
import { measurePeaks } from '../../utils/audioPeaks';
import { T, RADIUS, BODY_FONT, firstNameOf, formatTime } from './messagesModel';
import useLongPress from '../../hooks/useLongPress';
import useUserPref from '../../hooks/useUserPref';
import { useFan, FanLayer } from '../chrome/CreateFan';
import { flyToDock } from './messagesMotion';
import { voiceSkipOptions, voiceSkipStep } from '../../utils/skipRamp';
import VoiceTranscript from './VoiceTranscript';
import {
  LISTEN_SAVE_MS, SKIP_HOLD_MS, NOTES_MAX_PX, NOTES_MAX_KEYBOARD_PX, RATES, nextRate, rateLabel,
  addRange, mergeRanges, clampSeek, listenedFraction, fullyHeard, formatStamp,
  matchNotes, stampMatches, highlightParts, searchCountLine,
} from './voiceNoteModel';

/** The colours of the player: heard is ink, unheard is the day-pill grey, the playhead is the beam. */
/** Heard, behind the playhead: deep cyan, the beam's darker sibling. Not ink. */
const HEARD = '#0A9BA6';
/** Never heard — light enough that a skipped gap reads as a hole. */
const UNHEARD = '#DEE1E6';
/** Heard on an earlier listen, ahead of the playhead. */
const HEARD_AHEAD = '#A6DDE1';
const PLAYHEAD = T.beam;
const TICK = T.live;
const FAN_ACCENT = '#00FFFF';
/** A just-saved note flashes this until the next save. */
const FRESH = T.activeBadge;

/**
 * The docked voice-note player (Oct 2026; rebuilt to the voice-notes handoff, Part C). A tap
 * on a voice note in the thread flies it up here, under the thread header, and it plays
 * from the dock:
 *
 * - Row 1: (a back chevron while the thread header is collapsed) · the sender · `NOTE 0:42`
 *   (pins this second and turns the composer into the note field) · close.
 * - Row 2: back-skip · play · forward-skip · the waveform scrubber (ink heard / grey unheard,
 *   magenta ticks for your notes, grey for shared ones, the beam playhead).
 *   The skip buttons carry their step, and every tap is that step — no ramp, no running
 *   total: taps fire on pointer-up so they land however fast they come (a click is swallowed
 *   by the WebView's double-tap handling, which is what used to stop a fast burst dead).
 *   HOLD one and a fan of steps opens (`chrome/CreateFan`, the `skip` geometry) — slide onto
 *   a step and let go, or tap one. The pick is the account's `voiceSkipSec`.
 *   The clock sits under the waveform, at its right end.
 * - Row 3: ONE full-width strip, `1× | TRANSCRIPT | NOTES · n` — the speed cell (`1×` → `1.5×`
 *   → `2×` → `0.5×`, `voicePlaybackRate`) and the two drawers (they drop out of the dock,
 *   under everything else; a lit cell is an open drawer). It never wraps: the cells share the
 *   width and a label ellipsizes before the strip breaks. As separate pills they stacked onto
 *   two or three lines on a small phone.
 * - The notes list lives IN the dock (no floating card): search + scope (THIS NOTE | WHOLE
 *   CHAT) + filter chips when the chat has two or more notes, the count line, highlighted
 *   matches, the rows as before (stamp jumps, share toggle, hold yours → DELETE · KEEP).
 *   It is 196px tall, 108 while the keyboard is up, so the thread still shows the card.
 *
 * Writing a note is the COMPOSER's job now (iOS keyboard): `startNote()` pins the second and
 * tells the thread, which switches the composer to note mode; `saveNote(stamp, body)` is
 * called back from there. Heard ranges stay in audio time, so speed never changes what
 * counts as heard; they are saved every few seconds while playing and on every stop.
 */
const VoicePlayer = forwardRef(function VoicePlayer({
  msg,
  threadId = null,
  fromRect = null,
  fromEl = null,
  listened = [],
  onListened,
  onClose,
  /** ({ playing, at }) — the card's live label; fired on play/pause and once a second. */
  onState,
  isMobile = false,
  ephemeral = false,
  /** Open the notes list as soon as the dock is up (the card's note badge was tapped). */
  notesOpenInitially = false,
  /** (messageId, count) — how many notes the viewer can read now; the card's badge. */
  onNotesChanged,
  /** The waveform string, when known. */
  peaks = null,
  /** (messageId, peaks) — this browser measured a note that had none. */
  onPeaks,
  /** The viewer sent this note (`YOU` in the dock; `YOU` on their groups in WHOLE CHAT). */
  mine = false,
  viewerUserId = null,
  /** The other person's name, for the `REZA’S` chip and the WHOLE CHAT group labels. */
  peerName = '',
  /** Start here (a WHOLE CHAT result in another voice note landed on this one). */
  startAt = null,
  /** The thread header is collapsed: show the back chevron that brings it back. */
  headerHidden = false,
  onShowHeader = null,
  /** Note mode: the composer is the note field right now. `onStartNote(stamp)` asks for it. */
  noteMode = false,
  onStartNote = null,
  /** WHOLE CHAT search state lives with the thread so it survives switching voice notes. */
  search = { query: '', scope: 'note', filter: 'all' },
  onSearchChange = null,
  onSearchFocus = null,
  searchFocusInitially = false,
  /** The keyboard is up (a field has focus on a phone): the list shrinks. */
  keyboardUp = false,
  /** (messageId, atSec) — a result in another voice note: load it in the dock there. */
  onJumpToVoice = null,
  onToast = null,
}, ref) {
  const audioRef = useRef(null);
  const dockRef = useRef(null);
  const searchRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [at, setAt] = useState(0);
  const [metaDuration, setMetaDuration] = useState(0);
  const [ranges, setRanges] = useState(() => mergeRanges(listened, msg.audioDurationSec || 0));
  const [notes, setNotes] = useState([]);
  const [notesOpen, setNotesOpen] = useState(notesOpenInitially || searchFocusInitially);
  const [freshId, setFreshId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [failed, setFailed] = useState(false);
  const [chatNotes, setChatNotes] = useState(null);     // ThreadVoiceNotesDto, once fetched
  const [skipPref, setSkipPref] = useUserPref('voiceSkipSec');
  const [rate, setRate] = useUserPref('voicePlaybackRate');
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [fanHover, setFanHover] = useState(null);
  const [fanDir, setFanDir] = useState(1);
  const [searchFocused, setSearchFocused] = useState(false);
  // Replies (Oct 2026): which threads are open, and what is half-written in each.
  const [openReplies, setOpenReplies] = useState({});
  const [replyDrafts, setReplyDrafts] = useState({});
  const fan = useFan();

  const duration = msg.audioDurationSec || metaDuration || 0;
  const step = voiceSkipStep(duration, skipPref);
  const freshTimer = useRef(null);
  useEffect(() => () => clearTimeout(freshTimer.current), []);
  const rangesRef = useRef(ranges);
  const segStart = useRef(null);
  const dirty = useRef(false);
  const lastReported = useRef(0);
  const lastState = useRef('');
  const notesLoaded = useRef(false);

  // ── Heard ranges: accumulate while playing, save on a timer and on every stop. ──
  const commitSegment = useCallback((to) => {
    const from = segStart.current;
    if (from == null) return;
    segStart.current = null;
    const next = addRange(rangesRef.current, from, to, duration);
    if (next.length !== rangesRef.current.length || next.some((r, i) => r[0] !== rangesRef.current[i][0] || r[1] !== rangesRef.current[i][1])) {
      rangesRef.current = next;
      dirty.current = true;
      setRanges(next);
    }
  }, [duration]);

  const persist = useCallback(() => {
    if (ephemeral || !dirty.current) return;
    dirty.current = false;
    messageService.saveVoiceListened?.(msg.id, rangesRef.current);
  }, [ephemeral, msg.id]);

  const report = useCallback((force = false) => {
    const now = Date.now();
    if (!force && now - lastReported.current < 800) return;
    lastReported.current = now;
    onListened?.(msg.id, rangesRef.current);
  }, [msg.id, onListened]);

  useEffect(() => {
    if (!playing) return undefined;
    const t = setInterval(() => { commitSegment(audioRef.current?.currentTime || 0); segStart.current = audioRef.current?.currentTime ?? null; persist(); }, LISTEN_SAVE_MS);
    return () => clearInterval(t);
  }, [playing, commitSegment, persist]);

  // Closing, switching notes or leaving the page all save what was heard.
  useEffect(() => () => {
    const a = audioRef.current;
    if (a && segStart.current != null) {
      rangesRef.current = addRange(rangesRef.current, segStart.current, a.currentTime, duration);
      segStart.current = null;
      dirty.current = true;
    }
    persist();
    onListened?.(msg.id, rangesRef.current);
    if (a) { try { a.pause(); a.removeAttribute('src'); a.load(); } catch { /* already gone */ } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Open: fly in from the card, fetch the notes + server ranges, start playing. ──
  useLayoutEffect(() => {
    flyToDock({ dock: dockRef.current, fromRect, fromEl });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const a = audioRef.current;
    if (a) {
      if (startAt != null) { try { a.currentTime = startAt; } catch { /* metadata not loaded yet */ } setAt(startAt); }
      a.play().catch(() => {});
    }
    if (ephemeral) return;
    let alive = true;
    messageService.getVoiceNote?.(msg.id).then((r) => {
      if (!alive || !r?.success || !r.data) return;
      setNotes(r.data.notes || []);
      notesLoaded.current = true;
      onNotesChanged?.(msg.id, (r.data.notes || []).length);
      const merged = mergeRanges([...rangesRef.current, ...(r.data.listenedRanges || [])], duration);
      rangesRef.current = merged;
      setRanges(merged);
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msg.id]);
  // A late metadata load lands the requested start.
  const startedAtRef = useRef(startAt);
  useEffect(() => {
    if (startAt == null || startedAtRef.current === startAt) return;
    startedAtRef.current = startAt;
    const a = audioRef.current;
    if (a) { try { a.currentTime = startAt; } catch { /* not seekable yet */ } setAt(startAt); if (a.paused) a.play().catch(() => {}); }
  }, [startAt]);

  // ── Waveform backfill: an older note has no peaks; decode it once and keep them. ──
  useEffect(() => {
    if (peaks || ephemeral || !msg.id) return undefined;
    let alive = true;
    (async () => {
      const r = await messageService.getVoiceAudio?.(msg.id);
      if (!alive || !r?.success || !r.data) return;
      const measured = await measurePeaks(r.data);
      if (!alive || !measured) return;
      onPeaks?.(msg.id, measured);
      messageService.saveVoicePeaks?.(msg.id, measured);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msg.id, !!peaks, ephemeral]);

  // ── Speed: the element follows the pref; pitch is kept. Heard ranges stay in audio time. ──
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    try {
      a.playbackRate = RATES.includes(rate) ? rate : 1;
      if ('preservesPitch' in a) a.preservesPitch = true;
      else if ('webkitPreservesPitch' in a) a.webkitPreservesPitch = true;
    } catch { /* an engine that refuses the rate */ }
  }, [rate]);

  // ── Transport ──
  const seek = useCallback((t) => {
    const a = audioRef.current;
    if (!a) return 0;
    commitSegment(a.currentTime);
    const next = clampSeek(t, duration);
    try { a.currentTime = next; } catch { /* metadata not loaded yet */ }
    setAt(next);
    if (!a.paused) segStart.current = next;
    return next;
  }, [commitSegment, duration]);
  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      if (a.ended || (duration > 0 && a.currentTime >= duration - 0.05)) { try { a.currentTime = 0; } catch { /* ignore */ } }
      a.play().catch(() => setPlaying(false));
    } else a.pause();
  }, [duration]);
  const play = useCallback(() => { const a = audioRef.current; if (a && a.paused) a.play().catch(() => setPlaying(false)); }, []);

  // A skip tap: always the chosen step. Nothing accumulates and nothing is announced — the
  // step is on the button, and taps are meant to be spammed.
  const skipTap = useCallback((dir) => {
    const before = audioRef.current?.currentTime || 0;
    seek(before + dir * step);
  }, [step, seek]);
  /** The arrow keys and the card's seek use the same step. */
  const skip = useCallback((delta) => skipTap(delta < 0 ? -1 : 1), [skipTap]);

  // ── The fan: hold a skip button, pick a step by sliding or tapping. ──
  const options = voiceSkipOptions(duration);
  const fanItems = useMemo(() => options.map((o) => ({ key: String(o), label: `${o} SEC`, accent: FAN_ACCENT, value: o })), [options]);
  const pickSkip = useCallback((value) => {
    setSkipPref(value);
    fan.dismiss();
    setFanHover(null);
    onToast?.(`SKIPS ${value} SECONDS`);
  }, [setSkipPref, fan, onToast]);
  const openFan = useCallback((dir, el) => {
    setFanDir(dir);
    setFanHover(null);
    fan.open(el);
    try { navigator.vibrate?.(8); } catch { /* no haptics */ }
    // Slide onto a slab and let go to pick it; otherwise the fan stays for a tap.
    const move = (ev) => {
      const target = document.elementFromPoint(ev.clientX, ev.clientY);
      const slab = target?.closest?.('[data-fan-item]');
      setFanHover(slab ? slab.getAttribute('data-fan-item') : null);
    };
    const up = (ev) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      const target = document.elementFromPoint(ev.clientX, ev.clientY);
      const slab = target?.closest?.('[data-fan-item]');
      if (slab) pickSkip(Number(slab.getAttribute('data-fan-item')));
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }, [fan, pickSkip]);

  // ── Notes ──
  const startNote = useCallback(() => {
    const stamp = Math.floor(audioRef.current?.currentTime || 0);
    setNotesOpen(true);
    setConfirmId(null);
    onStartNote?.(stamp);
  }, [onStartNote]);
  const refreshChatNotes = useCallback(async () => {
    if (!threadId || ephemeral) return;
    const r = await messageService.getThreadVoiceNotes?.(threadId);
    if (r?.success && r.data) setChatNotes(r.data);
  }, [threadId, ephemeral]);
  const saveNote = useCallback(async (stamp, body, shared = false) => {
    if (ephemeral) return { success: false, error: 'Wait for the note to send.' };
    const atSec = stamp ?? Math.floor(audioRef.current?.currentTime || 0);
    const r = await messageService.addVoiceNoteNote?.(msg.id, atSec, (body || '').trim(), shared);
    if (r?.success && r.data) {
      setNotes((prev) => [...prev, r.data].sort((x, y) => x.atSec - y.atSec || new Date(x.createdAt) - new Date(y.createdAt)));
      setFreshId(r.data.id);
      clearTimeout(freshTimer.current);
      freshTimer.current = setTimeout(() => setFreshId(null), 2200);
      setNotesOpen(true);
      // The new row has to be among the ones on screen to flash: back to THIS NOTE, no filter.
      onSearchChange?.({ scope: 'note', filter: 'all', query: '' });
      refreshChatNotes();
      return { success: true, note: r.data };
    }
    return { success: false, error: r?.error?.message || r?.message || 'Could not save that note.' };
  }, [ephemeral, msg.id, refreshChatNotes, onSearchChange]);
  const deleteNote = async (id, messageId = msg.id) => {
    if (messageId === msg.id) setNotes((prev) => prev.filter((n) => n.id !== id));
    setChatNotes((prev) => (prev ? { ...prev, totalNotes: Math.max(0, prev.totalNotes - 1), groups: prev.groups.map((g) => ({ ...g, notes: g.notes.filter((n) => n.id !== id) })).filter((g) => g.notes.length) } : prev));
    await messageService.deleteVoiceNoteNote?.(messageId, id);
  };
  // Share with the thread or take it back: optimistic, reconciled with what the server stored.
  const shareNote = async (id, shared, messageId = msg.id) => {
    const patch = (list) => list.map((n) => (n.id === id ? { ...n, shared } : n));
    if (messageId === msg.id) setNotes(patch);
    setChatNotes((prev) => (prev ? { ...prev, groups: prev.groups.map((g) => ({ ...g, notes: patch(g.notes) })) } : prev));
    const r = await messageService.setVoiceNoteNoteShared?.(messageId, id, shared);
    if (r?.success && r.data) { if (messageId === msg.id) setNotes((prev) => prev.map((n) => (n.id === id ? { ...n, ...r.data } : n))); }
    else if (r && !r.success) { if (messageId === msg.id) setNotes((prev) => prev.map((n) => (n.id === id ? { ...n, shared: !shared } : n))); }
  };

  // A reply rides on the note's own visibility: the server refuses one on a private note
  // that isn't yours, which is also the only note you could never have seen.
  const sendReply = async (note) => {
    const body = (replyDrafts[note.id] || '').trim();
    if (!body) return;
    const messageId = note.messageId || msg.id;
    setReplyDrafts((d) => ({ ...d, [note.id]: '' }));
    const r = await messageService.addVoiceNoteReply?.(messageId, note.id, body);
    if (r?.success && r.data) {
      const add = (list) => list.map((n) => (n.id === note.id ? { ...n, replies: [...(n.replies || []), r.data] } : n));
      if (messageId === msg.id) setNotes(add);
      setChatNotes((prev) => (prev ? { ...prev, groups: prev.groups.map((g) => ({ ...g, notes: add(g.notes) })) } : prev));
    } else {
      setReplyDrafts((d) => ({ ...d, [note.id]: body }));
      onToast?.((r?.error?.message || r?.error || 'THAT DIDN’T SEND').toString().toUpperCase());
    }
  };

  useImperativeHandle(ref, () => ({ toggle, seek, skip, play, startNote, saveNote, open: () => setNotesOpen(true), focusSearch: () => { setNotesOpen(true); setTimeout(() => searchRef.current?.focus(), 0); } }), [toggle, seek, skip, play, startNote, saveNote]);

  const notesCount = notes.length;
  useEffect(() => {
    if (!notesLoaded.current) return;
    onNotesChanged?.(msg.id, notesCount);
  }, [notesCount, msg.id, onNotesChanged]);

  useEffect(() => {
    const key = `${playing}:${Math.floor(at)}`;
    if (key === lastState.current) return;
    lastState.current = key;
    onState?.({ playing, at: Math.floor(at) });
  }, [playing, at, onState]);

  // ── Search (§4b): the field shows once the chat holds two or more notes. ──
  const wholeChat = search.scope === 'chat';
  const query = search.query || '';
  const filter = search.filter || 'all';
  const hasQuery = !!query.trim();
  useEffect(() => { if (notesOpen && chatNotes == null) refreshChatNotes(); }, [notesOpen, chatNotes, refreshChatNotes]);
  const allNotesCount = chatNotes ? Math.max(chatNotes.totalNotes, notes.length) : notes.length;
  const canSearch = allNotesCount > 1;
  const chatRows = useMemo(() => {
    if (!wholeChat) return null;
    const groups = chatNotes?.groups || [];
    // The dock's own copy of THIS note's list is the fresher one.
    return groups.map((g) => ({ ...g, notes: g.messageId === msg.id ? notes : g.notes }));
  }, [wholeChat, chatNotes, msg.id, notes]);
  const shown = useMemo(() => {
    if (!wholeChat) return matchNotes(notes, query, filter).map((n) => ({ ...n, messageId: msg.id }));
    return (chatRows || []).flatMap((g) => matchNotes(g.notes, query, filter).map((n) => ({ ...n, messageId: g.messageId, group: g })));
  }, [wholeChat, notes, chatRows, query, filter, msg.id]);
  const filtered = hasQuery || filter !== 'all';
  const matchedIds = useMemo(() => new Set(shown.filter((n) => n.messageId === msg.id).map((n) => n.id)), [shown, msg.id]);
  const jumpTo = useCallback((messageId, atSec) => {
    if (messageId === msg.id) { seek(atSec); play(); return; }
    onJumpToVoice?.(messageId, atSec);
  }, [msg.id, seek, play, onJumpToVoice]);
  const jumpFirst = () => {
    if (!shown.length || !filtered) return;
    jumpTo(shown[0].messageId, shown[0].atSec);
    searchRef.current?.blur();
  };
  const setSearch = (patch) => { onSearchChange?.(patch); setConfirmId(null); };

  const onKey = (e) => {
    if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') return;
    if (e.key === 'ArrowLeft') { e.preventDefault(); skipTap(-1); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); skipTap(1); }
    else if (e.key === ' ') { e.preventDefault(); toggle(); }
    else if (e.key === 'Escape') onClose?.();
  };

  const senderFirst = firstNameOf(msg.senderDisplayName) || 'VOICE NOTE';
  const theirName = (firstNameOf(peerName) || (mine ? 'them' : senderFirst) || 'them').toUpperCase();
  // `firstNameOf` shouts (it is chrome copy); a sentence wants the name spoken normally.
  const spoken = (n) => (n ? n.charAt(0) + n.slice(1).toLowerCase() : '');
  /** The person a shared note reaches, in a sentence: "Tap to share with Reza". */
  const shareWith = spoken(firstNameOf(peerName) || (mine ? '' : firstNameOf(msg.senderDisplayName))) || 'the thread';
  const heardAll = fullyHeard(ranges, duration);
  const pct = duration > 0 ? Math.min(100, (at / duration) * 100) : 0;
  const pill = (on) => ({ height: 32, padding: '0 10px', borderRadius: RADIUS.pill, border: `2px solid ${T.ink}`, background: on ? T.ink : T.white, color: on ? T.white : T.ink, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, fontSize: 9, fontWeight: 700, letterSpacing: '.1em', cursor: 'pointer', transition: 'background-color 200ms, color 200ms' });
  // One cell of the speed | TRANSCRIPT | NOTES strip: the border and the radius belong to the
  // strip, so a cell is just a fill that lights when it is on.
  const seg = (on) => ({ flex: '1 1 0', minWidth: 0, justifyContent: 'center', padding: '0 8px', border: 0, background: on ? T.ink : T.white, color: on ? T.white : T.ink, display: 'flex', alignItems: 'center', fontSize: 9, fontWeight: 700, letterSpacing: '.08em', whiteSpace: 'nowrap', cursor: 'pointer', transition: 'background-color 200ms, color 200ms' });
  const keepFocus = (e) => e.preventDefault();

  return (
    <>
      <div
        ref={dockRef}
        data-voice-dock
        role="region"
        aria-label={`Voice note from ${senderFirst}`}
        tabIndex={0}
        onKeyDown={onKey}
        className="flex-shrink-0 outline-none"
        style={{
          margin: isMobile ? '10px 12px 0' : '12px 20px 0', background: T.white, borderRadius: 22,
          padding: isMobile ? '12px 14px 14px' : '14px 18px 16px', boxShadow: '0 6px 24px rgba(0,0,0,.08)',
          // The card has a ceiling and the notes list scrolls inside it, so a chat full of
          // notes can never push the conversation off the screen.
          display: 'flex', flexDirection: 'column', maxHeight: 560, minHeight: 0,
        }}
      >
        {/* Row 1 */}
        <div className="flex items-center" style={{ gap: 8 }}>
          {headerHidden && onShowHeader && (
            <button type="button" onClick={onShowHeader} aria-label="Show chat header" data-voice-show-header className="flex items-center justify-center flex-shrink-0" style={{ width: 28, height: 28, border: 0, background: 'none', padding: 0, cursor: 'pointer' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="square"><path d="M15 5l-7 7 7 7" /></svg>
            </button>
          )}
          <span className="flex items-center justify-center overflow-hidden flex-shrink-0 font-mono font-bold" style={{ width: 28, height: 28, borderRadius: RADIUS.small, background: T.activeBadge, fontSize: 11 }}>
            {msg.senderAvatarUrl ? <img src={msg.senderAvatarUrl} alt="" className="w-full h-full object-cover" draggable={false} /> : senderFirst.charAt(0)}
          </span>
          <div className="flex-1 min-w-0 font-mono font-bold truncate" style={{ fontSize: 10, letterSpacing: '.1em', color: T.grey500 }}>
            {mine ? 'YOU' : senderFirst.toUpperCase()}{heardAll ? ' · HEARD' : ''}
          </div>
          <button
            type="button"
            onClick={startNote}
            aria-label={`Write a note at ${formatStamp(at)}`}
            data-voice-note-button
            className="font-mono"
            style={pill(noteMode)}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinejoin="round" aria-hidden><path d="M4 20h4l10-10-4-4L4 16z" /></svg>
            <span className="tabular-nums">NOTE {formatStamp(at)}</span>
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close player"
            className="flex items-center justify-center flex-shrink-0 hover:bg-black hover:text-white"
            style={{ width: 32, height: 32, borderRadius: '50%', border: `2px solid ${T.ink}`, background: T.white, color: T.ink, padding: 0, cursor: 'pointer', transition: 'background-color 200ms, color 200ms' }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="square"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        {/* Row 2 */}
        <div className="flex items-center" style={{ gap: 8, marginTop: 12 }}>
          <SkipButton dir={-1} step={step} onTap={() => skipTap(-1)} onHold={(el) => openFan(-1, el)} />
          <button
            type="button"
            onClick={toggle}
            disabled={failed}
            aria-label={playing ? 'Pause' : 'Play'}
            className="flex items-center justify-center flex-shrink-0 disabled:opacity-40"
            style={{ width: 44, height: 44, borderRadius: '50%', background: T.ink, color: T.white, border: 0, cursor: 'pointer' }}
          >
            {playing
              ? <svg width="14" height="14" viewBox="0 0 12 12" fill="currentColor"><rect x="1" y="1" width="3.5" height="10" rx="1" /><rect x="7.5" y="1" width="3.5" height="10" rx="1" /></svg>
              : <svg width="14" height="14" viewBox="0 0 12 12" fill="currentColor" style={{ marginLeft: 2 }}><path d="M2 1l9 5-9 5z" /></svg>}
          </button>
          <SkipButton dir={1} step={step} onTap={() => skipTap(1)} onHold={(el) => openFan(1, el)} />

          <div className="flex flex-col flex-1 min-w-0" style={{ gap: 6 }}>
            <Scrubber
              at={at}
              duration={duration}
              ranges={ranges}
              notes={notes}
              dimUnmatched={filtered ? matchedIds : null}
              onSeek={seek}
              pct={pct}
              peaks={peaks}
            />
            <span className="self-end font-mono tabular-nums" data-voice-clock style={{ fontSize: 10, lineHeight: '12px', letterSpacing: '.06em', color: T.grey600 }}>
              {formatStamp(at)}{duration > 0 ? ` / ${formatStamp(duration)}` : ''}
            </span>
          </div>
        </div>

        {/* Row 3 — one strip, full width: speed | TRANSCRIPT | NOTES. Separate pills
            wrapped onto two or three lines on a small phone. Here the cells share the row
            (speed is narrow, the drawers split the rest) and nothing wraps; a label
            ellipsizes before the strip would ever break. */}
        <div
          data-voice-controls
          className="flex items-stretch"
          style={{ marginTop: 10, height: 40, borderRadius: RADIUS.pill, border: `2px solid ${T.ink}`, overflow: 'hidden', background: T.white }}
        >
          <button
            type="button"
            onClick={() => setRate(nextRate(rate))}
            aria-label={`Playback speed ${rateLabel(rate)}. Tap to change.`}
            data-voice-rate
            className="font-mono tabular-nums"
            style={{ ...seg(rate !== 1), flex: '0 0 auto', minWidth: 56, padding: '0 12px', fontSize: 11, fontWeight: 800, letterSpacing: '.02em' }}
          >
            {rateLabel(rate)}
          </button>
          <span aria-hidden style={{ width: 2, background: T.ink, flexShrink: 0 }} />
          <button
            type="button"
            onClick={() => setTranscriptOpen((o) => !o)}
            aria-expanded={transcriptOpen}
            aria-label={transcriptOpen ? 'Hide transcript' : 'Show transcript'}
            data-voice-transcript-toggle
            className="font-mono"
            style={seg(transcriptOpen)}
          >
            <span className="truncate">TRANSCRIPT</span>
          </button>
          <span aria-hidden style={{ width: 2, background: T.ink, flexShrink: 0 }} />
          <button
            type="button"
            onClick={() => { setNotesOpen((o) => !o); setConfirmId(null); }}
            aria-expanded={notesOpen}
            data-voice-notes-toggle
            className="font-mono tabular-nums"
            style={seg(notesOpen)}
          >
            <span className="truncate">NOTES · {notesCount}</span>
          </button>
        </div>

        {failed && (
          <div className="font-mono font-bold" style={{ marginTop: 8, fontSize: 9, letterSpacing: '.1em', color: T.danger }}>COULDN'T LOAD THIS VOICE NOTE.</div>
        )}

        {/* The transcript, dropped out of the player itself */}
        {transcriptOpen && (
          <VoiceTranscript
            msg={msg}
            at={at}
            onSeek={(t) => { seek(t); play(); }}
            ephemeral={ephemeral}
            inline
            maxHeight={keyboardUp ? 120 : 220}
          />
        )}

        {/* The notes list, in the dock. Scope tabs, then (once the chat holds two notes) the
            search field and the filter chips, then the rows — the only scrolling part, so the
            tabs and the field stay put while you read. */}
        {notesOpen && (
          <div data-voice-notes className="flex flex-col" style={{ marginTop: 12, gap: 8, flex: '1 1 auto', minHeight: 0 }}>
            <div className="flex items-center flex-shrink-0" style={{ gap: 18, padding: '0 2px' }}>
              {[['note', `THIS NOTE · ${notes.length}`], ['chat', `WHOLE CHAT · ${allNotesCount}`]].map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  onPointerDown={keepFocus}
                  onClick={() => setSearch({ scope: k })}
                  aria-pressed={search.scope === k}
                  data-voice-scope={k}
                  className="font-mono font-bold whitespace-nowrap"
                  style={{ border: 0, background: 'transparent', padding: '4px 0', fontSize: 10, letterSpacing: '.1em', cursor: 'pointer', color: search.scope === k ? T.ink : T.grey400, borderBottom: `2px solid ${search.scope === k ? T.ink : 'transparent'}`, transition: 'color 200ms' }}
                >
                  {l}
                </button>
              ))}
            </div>
            {canSearch && (
              <div className="flex flex-col flex-shrink-0" style={{ gap: 8 }}>
                <div className="flex items-center" style={{ height: 38, borderRadius: RADIUS.pill, background: T.off, gap: 8, padding: '0 6px 0 14px', outline: searchFocused ? `2px solid ${T.ink}` : 'none' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.4" strokeLinecap="square" aria-hidden><circle cx="10.5" cy="10.5" r="6.5" /><path d="M15.5 15.5L21 21" /></svg>
                  <input
                    ref={searchRef}
                    value={query}
                    onChange={(e) => setSearch({ query: e.target.value })}
                    onFocus={() => { setSearchFocused(true); onSearchFocus?.(true); }}
                    onBlur={() => { setSearchFocused(false); onSearchFocus?.(false); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); jumpFirst(); } if (e.key === 'Escape') { e.stopPropagation(); setSearch({ query: '' }); } }}
                    placeholder={wholeChat ? `Search ${allNotesCount} notes in this chat` : `Search ${notes.length} notes`}
                    enterKeyHint="search"
                    aria-label={wholeChat ? 'Search notes in this chat' : 'Search notes on this voice note'}
                    data-voice-search
                    autoFocus={searchFocusInitially}
                    className="flex-1 min-w-0 border-0 bg-transparent outline-none"
                    style={{ height: 34, fontFamily: BODY_FONT, fontSize: 16, color: T.ink }}
                  />
                  {hasQuery && (
                    <button type="button" onPointerDown={keepFocus} onClick={() => setSearch({ query: '' })} aria-label="Clear search" className="flex items-center justify-center flex-shrink-0" style={{ width: 26, height: 26, borderRadius: '50%', border: 0, background: T.ink, padding: 0, cursor: 'pointer' }}>
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="square"><path d="M6 6l12 12M18 6L6 18" /></svg>
                    </button>
                  )}
                </div>
                <div className="flex" style={{ gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }}>
                  {[['all', 'ALL'], ['mine', 'YOURS'], ['theirs', `${theirName}’S`], ['bookmarks', 'BOOKMARKS']].map(([k, l]) => (
                    <button key={k} type="button" onPointerDown={keepFocus} onClick={() => setSearch({ filter: k })} aria-pressed={filter === k} data-voice-filter={k} className="font-mono font-bold whitespace-nowrap flex-shrink-0" style={{ height: 28, padding: '0 11px', borderRadius: RADIUS.pill, border: 0, background: filter === k ? T.ink : T.off, color: filter === k ? T.white : T.ink, fontSize: 9, letterSpacing: '.1em', cursor: 'pointer', transition: 'background-color 200ms, color 200ms' }}>{l}</button>
                  ))}
                </div>
              </div>
            )}
            {canSearch && (filtered || wholeChat) && (
              <span data-voice-search-count className="font-mono font-bold flex-shrink-0" style={{ fontSize: 9, letterSpacing: '.1em', color: T.grey500, padding: '0 4px' }}>
                {searchCountLine({ shown, total: wholeChat ? allNotesCount : notes.length, wholeChat, hasQuery, filter })}
              </span>
            )}
            <div className="flex flex-col overflow-y-auto" style={{ flex: '1 1 auto', minHeight: 0, maxHeight: keyboardUp ? NOTES_MAX_KEYBOARD_PX : NOTES_MAX_PX, margin: '0 -4px', padding: '0 4px' }}>
              {filtered && !shown.length && (
                <span data-voice-no-match style={{ fontSize: 13, lineHeight: 1.5, color: T.grey500, padding: '8px 2px', fontFamily: BODY_FONT }}>Nothing matches. Try another word or filter.</span>
              )}
              {!filtered && !shown.length && (
                <span style={{ fontSize: 13, lineHeight: 1.5, color: T.grey500, padding: '8px 2px', fontFamily: BODY_FONT }}>No notes yet. Tap NOTE to pin one to the second you’re on.</span>
              )}
              {shown.map((n, i) => {
                const isMine = n.mine !== false;
                const groupStart = wholeChat && (i === 0 || shown[i - 1].messageId !== n.messageId);
                const g = n.group;
                const replies = n.replies || [];
                const repliesOpen = !!openReplies[n.id];
                // Reply to a note you can read: one you shared, or one of theirs. A private
                // note of yours has nobody to talk to yet.
                const canReply = !!n.shared || !isMine;
                const near = n.messageId === msg.id && Math.abs(at - n.atSec) < 1.2;
                const draft = replyDrafts[n.id] || '';
                const author = isMine ? 'YOU' : (firstNameOf(n.authorName) || theirName).toUpperCase();
                return (
                  <React.Fragment key={`${n.messageId}:${n.id}`}>
                    {groupStart && g && (
                      <span data-voice-group className="flex items-center font-mono font-bold" style={{ gap: 6, padding: '10px 2px 4px', fontSize: 9, letterSpacing: '.1em', color: n.messageId === msg.id ? T.ink : T.grey400 }}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" aria-hidden><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /></svg>
                        {viewerUserId && g.senderId === viewerUserId ? 'YOU' : (firstNameOf(g.senderDisplayName) || 'THEM').toUpperCase()} · {formatTime(g.createdAt)} · {formatStamp(g.durationSec || 0)}{n.messageId === msg.id ? ' · THIS ONE' : ''}
                      </span>
                    )}
                    <NoteRow
                      note={n}
                      mine={isMine}
                      fresh={freshId === n.id}
                      confirming={confirmId === n.id}
                      onConfirm={setConfirmId}
                      onDelete={(id) => deleteNote(id, n.messageId)}
                      repliesOpen={repliesOpen}
                      below={repliesOpen && (
                        <div className="flex flex-col" style={{ gap: 10, padding: '2px 8px 10px 60px' }}>
                          {replies.map((r) => (
                            <div key={r.id} className="flex items-start" style={{ gap: 8 }}>
                              <span className="flex items-center justify-center flex-shrink-0 font-mono font-bold" style={{ width: 20, height: 20, borderRadius: 5, background: r.mine ? T.ink : T.dayPill, color: r.mine ? T.white : T.ink, fontSize: 9 }}>
                                {(r.mine ? 'You' : (firstNameOf(r.authorName) || '?')).charAt(0).toUpperCase()}
                              </span>
                              <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 2 }}>
                                <span className="font-mono font-bold" style={{ fontSize: 9, letterSpacing: '.1em', color: T.grey500 }}>
                                  {r.mine ? 'YOU' : (firstNameOf(r.authorName) || 'THEM').toUpperCase()} · {formatTime(r.createdAt)}
                                </span>
                                <span style={{ fontSize: 13, lineHeight: 1.4, fontFamily: BODY_FONT, wordBreak: 'break-word' }}>{r.body}</span>
                              </div>
                            </div>
                          ))}
                          <div className="flex items-center" style={{ height: 36, borderRadius: RADIUS.pill, background: T.off, gap: 6, padding: '0 4px 0 12px' }}>
                            <input
                              value={draft}
                              onChange={(e) => { const v = e.target.value; setReplyDrafts((d) => ({ ...d, [n.id]: v })); }}
                              onFocus={() => onSearchFocus?.(true)}
                              onBlur={() => onSearchFocus?.(false)}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendReply(n); } }}
                              placeholder={`Reply to ${isMine ? 'your note' : (spoken(firstNameOf(n.authorName) || firstNameOf(peerName)) || 'them')}`}
                              maxLength={500}
                              enterKeyHint="send"
                              aria-label="Reply to this note"
                              data-voice-reply-field={n.id}
                              className="flex-1 min-w-0 border-0 bg-transparent outline-none"
                              style={{ height: 32, fontFamily: BODY_FONT, fontSize: 16, color: T.ink }}
                            />
                            <button type="button" onPointerDown={keepFocus} onClick={() => sendReply(n)} aria-label="Send reply" data-voice-reply-send className="flex items-center justify-center flex-shrink-0" style={{ width: 28, height: 28, borderRadius: '50%', border: 0, background: draft.trim() ? T.ink : '#C4C8CE', padding: 0, cursor: 'pointer', transition: 'background-color 200ms' }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="square"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
                            </button>
                          </div>
                        </div>
                      )}
                    >
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); jumpTo(n.messageId, n.atSec); }}
                        aria-label={`Jump to ${formatStamp(n.atSec)}`}
                        className="flex-shrink-0 font-mono font-bold tabular-nums hover:bg-black hover:text-white"
                        style={{ height: 24, minWidth: 44, padding: '0 8px', marginTop: 1, borderRadius: RADIUS.pill, border: 0, color: near ? T.white : T.ink, background: near ? T.ink : stampMatches(n.atSec, query) ? FRESH : T.off, fontSize: 10, letterSpacing: '.04em', cursor: 'pointer', transition: 'background-color 200ms, color 200ms' }}
                      >
                        {formatStamp(n.atSec)}
                      </button>
                      <div className="flex-1 min-w-0 flex flex-col items-start" style={{ gap: 5 }}>
                        {n.body
                          ? <span role="button" tabIndex={0} onClick={() => jumpTo(n.messageId, n.atSec)} onKeyDown={(e) => { if (e.key === 'Enter') jumpTo(n.messageId, n.atSec); }} style={{ fontSize: 14, lineHeight: 1.4, fontFamily: BODY_FONT, whiteSpace: 'pre-wrap', wordBreak: 'break-word', cursor: 'pointer' }}>
                            {highlightParts(n.body, query).map((p, k) => <span key={k} style={p.hit ? { background: FRESH, fontWeight: 700 } : undefined}>{p.text}</span>)}
                          </span>
                          : <span role="button" tabIndex={0} onClick={() => jumpTo(n.messageId, n.atSec)} onKeyDown={(e) => { if (e.key === 'Enter') jumpTo(n.messageId, n.atSec); }} className="font-mono font-bold" style={{ fontSize: 10, letterSpacing: '.12em', color: T.grey500, cursor: 'pointer', paddingTop: 4, background: hasQuery && 'bookmark'.includes(query.trim().toLowerCase()) ? FRESH : 'transparent' }}>BOOKMARK</span>}
                        <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
                          <span className="font-mono font-bold" style={{ fontSize: 9, letterSpacing: '.1em', color: T.grey400 }}>
                            {isMine ? (n.shared ? 'YOU · SHARED' : 'YOU · ONLY YOU') : author}
                          </span>
                          {canReply && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setOpenReplies((o) => ({ ...o, [n.id]: !o[n.id] })); }}
                              data-voice-replies-toggle
                              aria-expanded={repliesOpen}
                              className="font-mono font-bold"
                              style={{ border: 0, background: 'transparent', padding: '2px 0', fontSize: 9, letterSpacing: '.1em', color: T.ink, cursor: 'pointer' }}
                            >
                              {repliesOpen ? 'HIDE' : replies.length ? `${replies.length} ${replies.length === 1 ? 'REPLY' : 'REPLIES'}` : 'REPLY'}
                            </button>
                          )}
                        </div>
                      </div>
                      {isMine && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); shareNote(n.id, !n.shared, n.messageId); onToast?.(n.shared ? 'ONLY YOU CAN SEE IT' : `SHARED WITH ${theirName}`); }}
                          aria-label={n.shared ? `Shared with ${shareWith}. Tap to make it only yours` : `Only you. Tap to share with ${shareWith}`}
                          aria-pressed={!!n.shared}
                          data-voice-share
                          className="flex items-center justify-center flex-shrink-0 hover:bg-gray-100"
                          style={{ width: 32, height: 32, borderRadius: '50%', border: 0, background: 'transparent', color: n.shared ? T.ink : T.grey400, padding: 0, cursor: 'pointer', transition: 'color 200ms' }}
                        >
                          {n.shared
                            ? <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" aria-hidden><circle cx="8" cy="9" r="3" /><circle cx="16" cy="9" r="3" /><path d="M2 20c0-3 3-5 6-5s6 2 6 5M14 15.3c.6-.2 1.3-.3 2-.3 3 0 6 2 6 5" /></svg>
                            : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" aria-hidden><rect x="5" y="11" width="14" height="10" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>}
                        </button>
                      )}
                    </NoteRow>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        <audio
          ref={audioRef}
          src={msg.audioUrl}
          preload="metadata"
          onLoadedMetadata={(e) => { const d = e.currentTarget.duration; if (Number.isFinite(d) && d > 0) setMetaDuration(d); }}
          onPlay={(e) => { segStart.current = e.currentTarget.currentTime; setPlaying(true); setFailed(false); }}
          onPause={(e) => { commitSegment(e.currentTarget.currentTime); setPlaying(false); persist(); report(true); }}
          onEnded={(e) => { commitSegment(duration || e.currentTarget.currentTime); setPlaying(false); persist(); report(true); }}
          onSeeked={(e) => { if (!e.currentTarget.paused) segStart.current = e.currentTarget.currentTime; }}
          onTimeUpdate={(e) => {
            const t = e.currentTarget.currentTime;
            setAt(t);
            if (segStart.current != null && !e.currentTarget.paused) {
              const next = addRange(rangesRef.current, segStart.current, t, duration);
              if (next.length !== rangesRef.current.length || next[next.length - 1]?.[1] !== rangesRef.current[rangesRef.current.length - 1]?.[1]) {
                rangesRef.current = next; dirty.current = true; setRanges(next); report();
              }
            }
          }}
          onError={() => { setFailed(true); setPlaying(false); }}
          className="hidden"
        />
      </div>

      <FanLayer
        fan={fan}
        items={fanItems}
        origin="skip"
        anchorSize={40}
        selectedKey={String(step)}
        hoverKey={fanHover}
        caption={`SKIP BY · ${formatStamp(duration)} NOTE`}
        menuLabel="Skip by"
        onPick={(item) => pickSkip(item.value)}
        face={<SkipFace dir={fanDir} step={step} />}
      />
    </>
  );
});

export default VoicePlayer;

/**
 * A skip button: 40px, 2px ink, the rewind-arrow glyph with the live step inside. Tap =
 * one step; hold 380ms = the fan (the hold swallows the click that follows, so letting go
 * never also skips).
 */
/**
 * A skip button that keeps up with a thumb.
 *
 * The seek fires on POINTER-UP, not on click: a WebView withholds the click of a second tap
 * that lands within its double-tap window, so a fast burst used to go dead halfway through.
 * The click that follows is swallowed (`tapped`), and a click with no pointer before it — a
 * keyboard Enter — still seeks. A hold opens the step fan instead and cancels the tap.
 */
function SkipButton({ dir, step, onTap, onHold }) {
  const back = dir < 0;
  const ref = useRef(null);
  const held = useRef(false);
  const down = useRef(null);
  const tapped = useRef(0);
  const hold = useLongPress(() => { held.current = true; onHold?.(ref.current); }, { delay: SKIP_HOLD_MS });
  const onPointerDown = (e) => { held.current = false; down.current = { x: e.clientX, y: e.clientY }; };
  const onPointerUp = (e) => {
    const from = down.current;
    down.current = null;
    if (held.current) { held.current = false; return; }
    // A drag off the button is not a tap.
    if (from && (Math.abs(e.clientX - from.x) > 10 || Math.abs(e.clientY - from.y) > 10)) return;
    tapped.current = Date.now();
    onTap?.();
  };
  return (
    <button
      ref={ref}
      type="button"
      {...hold}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={() => { down.current = null; }}
      // The hold's capture handler has already cancelled the click that follows a long press.
      onClick={(e) => { if (e.defaultPrevented) return; if (Date.now() - tapped.current < 700) return; onTap?.(); }}
      aria-label={`${back ? 'Back' : 'Forward'} ${step} seconds. Hold to change.`}
      data-voice-skip={back ? 'back' : 'forward'}
      className="relative flex items-center justify-center flex-shrink-0 hover:bg-black hover:text-white select-none"
      style={{ width: 40, height: 40, borderRadius: '50%', border: `2px solid ${T.ink}`, background: T.white, color: T.ink, padding: 0, cursor: 'pointer', transition: 'background-color 200ms, color 200ms', touchAction: 'none', WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
    >
      <SkipGlyph back={back} step={step} />
    </button>
  );
}

function SkipGlyph({ back, step }) {
  return (
    <>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: back ? 'none' : 'scaleX(-1)' }} aria-hidden>
        <path d="M4 12a8 8 0 1 0 2.6-5.9" />
        <path d="M4 4v5h5" />
      </svg>
      <span className="absolute font-mono pointer-events-none" style={{ fontSize: 7, fontWeight: 800, letterSpacing: 0, top: 14, left: 0, right: 0, textAlign: 'center', lineHeight: 1 }}>{step}</span>
    </>
  );
}

/** The pressed skip button redrawn on the fan's scrim: black fill, white glyph + number. */
function SkipFace({ dir, step }) {
  return (
    <span className="relative flex items-center justify-center" style={{ width: 40, height: 40, borderRadius: '50%', background: T.ink, color: T.white, border: `2px solid ${T.ink}`, boxSizing: 'border-box' }}>
      <SkipGlyph back={dir < 0} step={step} />
    </span>
  );
}

/**
 * The scrubber: grey for what hasn't been heard, ink for what has, a beam-coloured playhead,
 * magenta ticks where the listener's notes sit (grey for shared ones). While a search or
 * filter is on, the ticks that don't match drop to 20%. Drag or tap anywhere to seek.
 */
function Scrubber({ at, duration, ranges, notes, onSeek, pct, peaks, dimUnmatched = null }) {
  const trackRef = useRef(null);
  const dragging = useRef(false);
  const toTime = (clientX) => {
    const r = trackRef.current?.getBoundingClientRect();
    if (!r || !r.width || !(duration > 0)) return null;
    return Math.min(1, Math.max(0, (clientX - r.left) / r.width)) * duration;
  };
  const onPointerDown = (e) => {
    if (!(duration > 0)) return;
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const t = toTime(e.clientX);
    if (t != null) onSeek(t);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    const t = toTime(e.clientX);
    if (t != null) onSeek(t);
  };
  const onPointerUp = (e) => {
    dragging.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };
  return (
    <div
      ref={trackRef}
      data-voice-scrubber
      role="slider"
      aria-label="Position"
      aria-valuemin={0}
      aria-valuemax={Math.round(duration)}
      aria-valuenow={Math.round(at)}
      aria-valuetext={`${formatStamp(at)} of ${formatStamp(duration)}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      className="relative w-full min-w-0 flex-none"
      style={{ height: 32, cursor: duration > 0 ? 'pointer' : 'default', touchAction: 'none' }}
    >
      <div className="absolute left-0 right-0" style={{ top: 0, height: 32 }}>
        <Waveform peaks={peaks} bars={36} gap={0.38} minLevel={0.18} ranges={ranges} duration={duration} at={at} heard={HEARD} ahead={HEARD_AHEAD} unheard={UNHEARD} height={32} />
      </div>
      {notes.map((n) => (
        <button
          key={n.id}
          type="button"
          aria-label={`${n.mine === false ? `${firstNameOf(n.authorName) || 'Their'} note` : 'Note'} at ${formatStamp(n.atSec)}`}
          title={n.body || 'Bookmark'}
          data-voice-tick
          data-dim={dimUnmatched && !dimUnmatched.has(n.id) ? 'true' : undefined}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onSeek(n.atSec); }}
          className="absolute"
          style={{ left: `calc(${duration > 0 ? (n.atSec / duration) * 100 : 0}% - 7px)`, top: 0, width: 14, height: 40, padding: 0, border: 0, background: 'transparent', cursor: 'pointer', opacity: dimUnmatched && !dimUnmatched.has(n.id) ? 0.2 : 1, transition: 'opacity 200ms' }}
        >
          {/* A dot below the bars — a line through them hid the heard colour it crossed. */}
          <span className="block" style={{ width: 6, height: 6, margin: '0 auto', marginTop: 31, borderRadius: '50%', background: n.mine === false ? T.grey400 : TICK }} />
        </button>
      ))}
      <span
        aria-hidden
        data-playhead
        className="absolute pointer-events-none"
        style={{ left: `calc(${pct}% - 7px)`, top: 9, width: 14, height: 14, borderRadius: '50%', background: PLAYHEAD, border: `2px solid ${T.ink}`, boxSizing: 'border-box' }}
      />
    </div>
  );
}

/**
 * One note in the list. Hold it (yours only) and a DELETE · KEEP strip takes the row over;
 * nothing on the row itself deletes, so a stray tap can't lose a note. A just-saved note
 * flashes pink until the next save.
 */
function NoteRow({ note: n, mine, fresh = false, confirming, onConfirm, onDelete, repliesOpen = false, below = null, children }) {
  const hold = useLongPress(() => { if (mine) onConfirm(n.id); }, { delay: 450 });
  // The strip is a sibling of the held element, not a child: the hold swallows the click
  // that follows it, and that click must land on KEEP or DELETE.
  return (
    <div
      data-voice-note
      data-mine={mine || undefined}
      data-shared={n.shared || undefined}
      data-fresh={fresh || undefined}
      data-confirming={confirming || undefined}
      data-replies-open={repliesOpen || undefined}
      className="relative"
      style={{ borderRadius: 14, background: confirming ? T.off : fresh ? FRESH : repliesOpen ? '#FAFAFA' : 'transparent', transition: 'background-color 300ms' }}
    >
      <div {...(mine ? hold : {})} className="flex items-start select-none" style={{ gap: 10, padding: '8px 4px 8px 6px', WebkitTouchCallout: 'none' }}>
        {children}
      </div>
      {below}
      {confirming && (
        <div className="absolute inset-0 flex items-center justify-end" style={{ gap: 6, padding: '0 4px 0 8px', background: `${T.off}F5`, borderRadius: 14 }} onClick={(e) => e.stopPropagation()}>
          <span className="font-mono font-bold" style={{ fontSize: 9, letterSpacing: '.1em', color: T.grey600, marginRight: 'auto' }}>DELETE THIS NOTE?</span>
          <button type="button" onClick={() => onConfirm(null)} className="font-mono font-bold" style={{ height: 28, padding: '0 10px', borderRadius: RADIUS.pill, border: `2px solid ${T.ink}`, background: 'transparent', color: T.ink, fontSize: 9, letterSpacing: '.1em', cursor: 'pointer' }}>KEEP</button>
          <button type="button" onClick={() => { onConfirm(null); onDelete(n.id); }} aria-label="Delete note" className="font-mono font-bold" style={{ height: 28, padding: '0 10px', borderRadius: RADIUS.pill, border: `2px solid ${T.danger}`, background: T.danger, color: T.white, fontSize: 9, letterSpacing: '.1em', cursor: 'pointer' }}>DELETE</button>
        </div>
      )}
    </div>
  );
}

/**
 * The voice note as it sits in a bubble: a play glyph, a bar painted ink where it has been
 * heard and grey where it hasn't, and the length. A tap hands it to the dock; while it is
 * up there the card shows the live clock instead of the length.
 */
export function VoiceNoteCard({ msg, ranges = [], active = false, playing = false, at = 0, mine = false, noteCount = 0, peaks = null, onPlay, onOpenNotes }) {
  const ref = useRef(null);
  const duration = msg.audioDurationSec || 0;
  const heard = listenedFraction(ranges, duration);
  const unheard = !mine && heard === 0;
  const label = active ? (playing ? `${formatStamp(at)} · PLAYING` : `${formatStamp(at)} · PAUSED`) : formatStamp(duration);
  return (
    <span className="flex items-center" style={{ gap: 8 }}>
    <button
      ref={ref}
      type="button"
      data-voice-card
      data-active={active || undefined}
      onClick={(e) => { e.stopPropagation(); onPlay?.(msg, ref.current?.getBoundingClientRect(), ref.current); }}
      aria-label={active ? (playing ? 'Pause voice note' : 'Play voice note') : `Play voice note, ${formatStamp(duration)}`}
      className="flex items-center text-left"
      style={{ gap: 10, minWidth: 190, maxWidth: 260, padding: 0, border: 0, background: 'transparent', color: 'inherit', cursor: 'pointer' }}
    >
      <span className="flex items-center justify-center flex-shrink-0" style={{ width: 34, height: 34, borderRadius: '50%', background: T.ink, color: T.white }}>
        {active && playing
          ? <EqBars />
          : <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" style={{ marginLeft: 1 }}><path d="M2 1l9 5-9 5z" /></svg>}
      </span>
      <span className="relative flex-1 block" style={{ height: 26, minWidth: 90 }}>
        <Waveform peaks={peaks || msg.audioPeaks} bars={36} ranges={ranges} duration={duration} heard={HEARD} unheard={mine ? 'rgba(0,0,0,.18)' : UNHEARD} height={26} />
      </span>
      <span className="flex items-center flex-shrink-0 font-mono tabular-nums" style={{ gap: 5, fontSize: 10, letterSpacing: '.08em', color: mine ? T.mineTime : T.grey600 }}>
        {label}
        {unheard && <span aria-label="Unheard" style={{ width: 7, height: 7, borderRadius: '50%', background: TICK, display: 'inline-block' }} />}
      </span>
    </button>
    {noteCount > 0 && (
      /* The note badge: there are notes on this one — yours, or ones shared with you. It
         opens the dock with the list open. */
      <button
        type="button"
        data-voice-note-badge
        onClick={(e) => { e.stopPropagation(); onOpenNotes?.(msg, ref.current?.getBoundingClientRect(), ref.current); }}
        aria-label={`${noteCount} note${noteCount === 1 ? '' : 's'} on this voice note`}
        className="relative flex items-center justify-center flex-shrink-0"
        style={{ width: 26, height: 26, borderRadius: '50%', border: 0, background: T.ink, color: T.white, cursor: 'pointer' }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h4l10-10-4-4L4 16z" /><path d="M12.5 7.5l4 4" /></svg>
        <span className="absolute font-mono font-bold flex items-center justify-center" style={{ top: -6, right: -7, minWidth: 16, height: 16, padding: '0 4px', borderRadius: RADIUS.pill, background: TICK, color: T.white, fontSize: 8, border: `2px solid ${mine ? T.mine : T.white}`, boxSizing: 'content-box' }}>{noteCount}</span>
      </button>
    )}
    </span>
  );
}

/** Three bars that bounce while the note plays in the dock. */
function EqBars() {
  return (
    <span className="flex items-end" style={{ gap: 2, height: 14 }} aria-hidden>
      {[0, 160, 320].map((d) => (
        <span key={d} className="pr-voice-eq" style={{ width: 3, height: 14, background: 'currentColor', borderRadius: 1.5, animationDelay: `${d}ms` }} />
      ))}
    </span>
  );
}
